// Forbrug og pris: Freemi koster 239 kr./md. inkl. moms, men SMS koster Freemi penge pr. stk. Reglen er, at kunden
// altid har betalt, før en SMS koster os noget:
//   • 200 SMS (dele) pr. måned er en del af abonnementet, som Stripe trækker forud hver måned. I prøven: 25 SMS.
//   • Derefter sendes SMS fra kundens SMS-saldo, som købes i pakker (0,69 kr. pr. SMS inkl. moms) og trækkes på kortet,
//     FØR SMS'erne sendes. Ejeren kan slå automatisk genopfyldning til (under 20 SMS → ny pakke) med et månedligt loft.
//   • Er der hverken kvote eller saldo (eller kortet afvises), sendes beskeden som mail i stedet, hvis vi har en e-mail.
//   • Spærre mod fejl og misbrug: højst 5 SMS pr. modtager pr. døgn, højst 300 pr. time pr. klient, kun danske numre.
//   • Login-koder sendes altid (sikkerhed) og tæller med.
// Alle beløb i øre. Kostpriser er Freemis egne (uden moms) og bruges til at vise overskud pr. klient i konsollen.
// Samme tal står i databasen (supabase/migrations/*_sms_saldo.sql) – en test sikrer, at de er ens.
import { PRIS, prisUdenMoms } from './abonnement.ts';

export const KVOTE = { sms_inkluderet: 200, sms_proeve: 25 };
export const SALDO = { sms_pris_inkl: 69, pakker: [100, 250, 500] as const, auto_pakke: 100, genopfyld_under: 20, loft_standard_inkl: 20700 };
export const SPAERRE = { pr_modtager_doegn: 5, pr_klient_time: 300 };
/** Freemis kostpris (uden moms). SMS: GatewayAPI, forsigtigt sat til 0,37 kr. pr. del (ret, når prislisten er hentet).
 *  Stripe: firmakort 2,8 % + 1,80 kr.; Billing 0,7 % af abonnementet. Drift: mail, AI og lager pr. klient (skøn). */
export const KOST = { sms: 37, rcs: 31, mail: 1, stripe_fast: 180, stripe_procent: 2.8, billing_procent: 0.7, drift_pr_klient: 700 };

/** Pris for en SMS-pakke (øre inkl. moms). */
export const pakkePris = (antal: number) => antal * SALDO.sms_pris_inkl;
/** Gyldig pakkestørrelse? */
export const erPakke = (antal: number) => (SALDO.pakker as readonly number[]).includes(antal);
/** Inkluderede SMS denne måned (færre i den gratis prøve). */
export const inkluderet = (proeve: boolean) => (proeve ? KVOTE.sms_proeve : KVOTE.sms_inkluderet);

export type SmsBeslutning = { send: 'sms' | 'mail' | 'stop' | 'koeb'; grund: string; fra_saldo: boolean };
export type SmsTilstand = {
  brugt: number;               // SMS-dele sendt denne måned
  proeve: boolean;             // klienten er i den gratis prøve
  saldo: number;               // forudbetalte SMS tilbage
  auto: boolean;               // automatisk genopfyldning slået til
  koebt_inkl: number;          // automatisk købt denne måned (øre inkl. moms)
  loft_inkl: number;           // loft for automatisk køb pr. måned (øre inkl. moms)
  modtager_doegn: number;      // SMS til samme nummer de sidste 24 timer
  klient_time: number;         // SMS fra klienten den sidste time
  har_mail: boolean;           // vi kender modtagerens e-mail (kan sende mail i stedet)
  login: boolean;              // login-kode: sendes altid
};

/** Må der købes en pakke automatisk lige nu (inden for ejerens loft)? */
export const kanAutoKoebe = (t: Pick<SmsTilstand, 'auto' | 'koebt_inkl' | 'loft_inkl'>) => t.auto && t.koebt_inkl + pakkePris(SALDO.auto_pakke) <= t.loft_inkl;
/** Skal saldoen fyldes op (i baggrunden, efter en SMS er sendt fra saldoen)? */
export const skalGenopfylde = (t: Pick<SmsTilstand, 'auto' | 'koebt_inkl' | 'loft_inkl' | 'saldo'>) => t.saldo < SALDO.genopfyld_under && kanAutoKoebe(t);

/** Må denne SMS sendes – eller skal der først købes en pakke, eller skal den sendes som mail / stoppes?
 *  dele = antal SMS-dele i teksten. 'koeb' betyder: træk en pakke på kortet, og spørg så igen. */
export function smsBeslutning(t: SmsTilstand, dele: number, til: string): SmsBeslutning {
  const ud = (grund: string): SmsBeslutning => ({ send: t.har_mail ? 'mail' : 'stop', grund, fra_saldo: false });
  const kvote = inkluderet(t.proeve);
  if (!/^(\+?45)?\d{8}$/.test(til.replace(/[\s\-]/g, ''))) return ud('Kun danske mobilnumre kan få SMS.');
  if (t.login) return { send: 'sms', grund: 'Login-kode', fra_saldo: t.brugt + dele > kvote && t.saldo >= dele };
  if (t.klient_time >= SPAERRE.pr_klient_time) return ud(`Over ${SPAERRE.pr_klient_time} SMS på en time – sat på pause for at undgå fejl.`);
  if (t.modtager_doegn >= SPAERRE.pr_modtager_doegn) return ud(`Modtageren har allerede fået ${SPAERRE.pr_modtager_doegn} SMS i dag.`);
  if (t.brugt + dele <= kvote) return { send: 'sms', grund: 'Inkluderet', fra_saldo: false };
  if (t.saldo >= dele) return { send: 'sms', grund: 'Fra SMS-saldoen', fra_saldo: true };
  if (kanAutoKoebe(t)) return { send: 'koeb', grund: 'Køb en SMS-pakke først', fra_saldo: false };
  if (t.auto) return ud('Loftet for automatisk køb af SMS denne måned er nået.');
  return ud(`De ${kvote} inkluderede SMS denne måned er brugt, og der er ingen SMS på saldoen.`);
}

/** Freemis økonomi for én klient i en måned (øre uden moms). koebt_inkl = SMS-pakker købt denne måned. */
export function maanedsRegnskab(f: { sms: number; rcs: number; mails: number; betalende: boolean; koebt_inkl?: number; koeb_antal?: number }) {
  const koebt = f.koebt_inkl ?? 0;
  const indtaegt_abon = f.betalende ? prisUdenMoms(PRIS.maaned_inkl) : 0;
  const indtaegt_sms = prisUdenMoms(koebt);
  const stripe = (f.betalende ? KOST.stripe_fast + PRIS.maaned_inkl * (KOST.stripe_procent + KOST.billing_procent) / 100 : 0)
    + (f.koeb_antal ?? 0) * KOST.stripe_fast + koebt * KOST.stripe_procent / 100;
  const sms_kost = f.sms * KOST.sms + f.rcs * KOST.rcs;
  const omkostning = Math.round(sms_kost + f.mails * KOST.mail + stripe + KOST.drift_pr_klient);
  const indtaegt = indtaegt_abon + indtaegt_sms;
  return { indtaegt, omkostning, overskud: indtaegt - omkostning, sms_kost };
}

/** Advarsler til ejeren ved 80 % og 100 % af kvoten (sendes én gang pr. måned pr. trin). */
export function kvoteTrin(brugt: number, proeve = false): 0 | 80 | 100 {
  const k = inkluderet(proeve);
  return brugt >= k ? 100 : brugt >= k * 0.8 ? 80 : 0;
}
